import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from '../BaseTool';
import { BIMElement } from '../../core/types';

/**
 * Match Type Properties Tool - BIM 2025 Style
 * 
 * Copies type properties from one element to another.
 * Works like a "paint picker" - select source, then apply to targets.
 * 
 * Workflow:
 * 1. Click source element (picks up type)
 * 2. Click target element(s) to apply the same type
 * 3. Press Escape to finish
 */
export class MatchTypeToolClass extends BaseTool {
    id = 'MATCH_TYPE';
    name = 'Match Type Properties';
    cursor = 'dropper';

    private sourceElement: BIMElement | null = null;
    private sourceType: string = '';

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const id = this.findElementIdAtPoint(event);
        if (!id) return;

        const element = this.context.bimStore.project.elements.get(id);
        if (!element) return;

        if (!this.sourceElement) {
            // First click: Pick source type
            this.sourceElement = element;
            this.sourceType = element.type;
            console.log('Match Type: Source picked -', this.sourceType, 'in category', element.category);

            // Visual feedback
            this.context.appStore.setToolPreview({
                active: true,
                toolId: this.id,
                sourceType: this.sourceType,
                sourceCategory: element.category
            });
        } else {
            // Subsequent clicks: Apply type to targets
            this.applyTypeToElement(element);
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.sourceElement) return;

        // Hover preview - show if element can receive the type
        const id = this.findElementIdAtPoint(event);
        if (id) {
            const element = this.context.bimStore.project.elements.get(id);
            if (element) {
                const canApply = element.category === this.sourceElement.category;

                this.context.appStore.setToolPreview({
                    active: true,
                    toolId: this.id,
                    sourceType: this.sourceType,
                    sourceCategory: this.sourceElement.category,
                    hoverElement: id,
                    canApply: canApply
                });
            }
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.reset();
            this.context?.appStore.setActiveTool('select');
        }
    }

    private applyTypeToElement(element: BIMElement): void {
        if (!this.context || !this.sourceElement) return;

        // Can only apply type to elements of the same category
        if (element.category !== this.sourceElement.category) {
            console.warn('Match Type: Cannot apply type to different category.',
                element.category, '!=', this.sourceElement.category);
            return;
        }

        // Don't apply to source itself
        if (element.id === this.sourceElement.id) {
            return;
        }

        // Copy type (family type name)
        this.context.bimStore.updateElement(element.id, {
            type: this.sourceType
        });

        // If source has specific parameters, copy those too
        if (this.sourceElement.parameters) {
            // Copy relevant type parameters (not instance parameters)
            const typeParams = this.getTypeParameters(this.sourceElement);
            if (Object.keys(typeParams).length > 0) {
                const existingParams = element.parameters || {};
                this.context.bimStore.updateElement(element.id, {
                    parameters: { ...existingParams, ...typeParams }
                });
            }
        }

        console.log('Match Type: Applied', this.sourceType, 'to', element.id);
    }

    private getTypeParameters(element: BIMElement): Record<string, any> {
        // Type parameters are typically things like:
        // - Material settings
        // - Construction type
        // - Standard sizes
        // Instance parameters (like location, dimensions) should not be copied

        const typeParamNames = [
            'Material',
            'Construction Type',
            'Fire Rating',
            'Structural Usage',
            'Manufacturer',
            'Model',
            'Type Name',
            'Assembly Code',
            'Type Mark',
            'Type Comments',
            'Cost'
        ];

        const typeParams: Record<string, any> = {};

        if (element.parameters) {
            for (const name of typeParamNames) {
                if (element.parameters[name] !== undefined) {
                    typeParams[name] = element.parameters[name];
                }
            }
        }

        return typeParams;
    }

    private reset(): void {
        this.sourceElement = null;
        this.sourceType = '';
        this.context?.appStore.setToolPreview(null);
    }

    deactivate(): void {
        super.deactivate();
        this.reset();
    }
}
